import React from 'react';
import { VIEWER_HOOKS } from '@wix/pricing-plans-common/hooks';
import { useStyles } from '@wix/tpa-settings/react';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import { ButtonDemo, TabState } from '../../../../constants/settings-events';
import { withClientSideRenderMark } from '../../../../contexts/ClientSideRenderMark';
import { useAdaptedSettings, useOpenCartAfterScroll, useStylesParams } from '../../../../hooks';
import { useShouldUseMobileSettings } from '../../../../hooks/useShouldUseMobileSettings';
import { ListProps } from '../../../../types/common';
import { alignmentName } from '../../../../utils/alignment';
import { Toast } from '../Toast';
import { EmptyState, EmptyStateProps } from './EmptyState';
import { ErrorState } from './ErrorState';
import { st, classes } from './List.st.css';
import { PlanList, MAX_ITEM_WIDTH } from './PlanList';

export interface ListWidgetProps extends ListProps {
  tabState: TabState;
  buttonDemo: ButtonDemo;
  areSettingsOpened: boolean;
  dataHook?: string;
  emptyState: EmptyStateProps;
  locale?: string;
  isCartEnabled?: boolean;
}

const List: React.FC<ListWidgetProps> = ({
  plans,
  variantEntries,
  isVariantSelectionEnabled,
  plansErrorMessage,
  retryLoadPlans,
  isRetryLoading,
  tabState,
  buttonDemo,
  areSettingsOpened,
  selectPlan,
  addToCart,
  dataHook,
  hideToast,
  emptyState,
  selectedPlanId,
  overrideAppTitle,
  overrideAppSubtitle,
  toast,
  isOnEcom,
  isCartEnabled,
  isAddToCartLoading,
  scrollToComp,
}) => {
  const { t } = useTranslation();
  const { isMobile, isEditorX } = useEnvironment();
  const stylesParams = useStylesParams();
  const settings = useAdaptedSettings();
  const styles = useStyles();

  const useMobileSettings = useShouldUseMobileSettings();

  const showPageTitle = styles.get(stylesParams.showPageTitle) || Boolean(overrideAppTitle);
  const showPageSubtitle =
    styles.get(stylesParams.showPageSubtitle) || settings.get('showPageSubtitle') || Boolean(overrideAppSubtitle);
  const showHeader = showPageTitle || showPageSubtitle;
  const TitleTag = settings.get('titleHtmlTag') as keyof React.JSX.IntrinsicElements;
  const extraPlanListPadding = 30;
  const variantEntryCount = Object.values(variantEntries ?? {}).flat().length;
  const entryCount = isVariantSelectionEnabled && variantEntryCount > 0 ? variantEntryCount : plans.length;
  const maxWidthStyle =
    !isEditorX && entryCount ? { maxWidth: MAX_ITEM_WIDTH * entryCount - extraPlanListPadding } : {};

  useOpenCartAfterScroll(scrollToComp?.shouldOpenCart, scrollToComp?.openSideCart);

  if (plansErrorMessage) {
    return <ErrorState onRetry={retryLoadPlans} isLoading={isRetryLoading} />;
  }

  if (entryCount === 0) {
    return <EmptyState {...emptyState} />;
  }

  return (
    <div
      className={st(classes.root, { mobile: !!isMobile, useMobileSettings })}
      data-hook={dataHook ?? VIEWER_HOOKS.PACKAGE_PICKER_WRAPPER}
    >
      {showHeader && (
        <div className={classes.header} style={maxWidthStyle}>
          {showPageTitle && (
            <TitleTag
              className={st(classes.title, { textAlignment: alignmentName(styles.get(stylesParams.titleAlignment)) })}
              data-hook="app-title"
            >
              {overrideAppTitle || settings.get('pageHeaderText') || t('pp.defaults.pageTitle')}
            </TitleTag>
          )}
          {showPageSubtitle && (
            <p
              className={st(classes.subtitle, {
                textAlignment: alignmentName(styles.get(stylesParams.subtitleAlignment)),
              })}
              data-hook={VIEWER_HOOKS.PP_WIDGET_SUBTITLE}
            >
              {overrideAppSubtitle || settings.get('pageSubtitleText') || t('pp.defaults.pageSubtitle')}
            </p>
          )}
        </div>
      )}
      <StatusToast hideToast={hideToast} toast={toast} />
      <PlanList
        selectPlan={selectPlan}
        addToCart={addToCart}
        plans={plans}
        variantEntries={variantEntries}
        isVariantSelectionEnabled={isVariantSelectionEnabled}
        buttonDemo={buttonDemo}
        showDemoHighlight={tabState === TabState.HIGHLIGHTED}
        expandMobileBenefits={areSettingsOpened}
        selectedPlanId={selectedPlanId}
        isOnEcom={isOnEcom}
        isCartEnabled={isCartEnabled}
        isAddToCartLoading={isAddToCartLoading}
      />
      {styles.get(stylesParams.showDisclaimer) && (
        <div
          className={st(classes.disclaimer, {
            textAlignment: alignmentName(styles.get(stylesParams.disclaimerAlignment)),
          })}
          style={maxWidthStyle}
        >
          <p>{settings.get('disclaimerText')}</p>
        </div>
      )}
    </div>
  );
};

type StatusToastProps = Pick<ListWidgetProps, 'toast' | 'hideToast'>;

const StatusToast = ({ toast, hideToast }: StatusToastProps) => {
  if (toast) {
    return (
      <Toast onClose={hideToast} informational={toast.informational}>
        {toast.message}
      </Toast>
    );
  }

  return null;
};

export default withClientSideRenderMark(List);
